import React from 'react';
import Link from 'next/link';

import styles from './styles.module.scss';

// 캡션 컴포넌트: 해시태그 및 멘션을 링크로 렌더링 + 개행 처리
interface CaptionProps {
  text: string | undefined | null;
}

const Caption: React.FC<CaptionProps> = ({ text }) => {
  if (!text) return null;

  // plain 텍스트 내 개행(\r?\n)을 <br/>로 변환하는 헬퍼
  const renderTextWithLineBreaks = (value: string, keyPrefix: string) => {
    const parts = value.split(/\r\n|\r|\n/);
    const nodes: React.ReactNode[] = [];
    parts.forEach((part, idx) => {
      if (part) {
        nodes.push(
          <span key={`${keyPrefix}${idx}`} className={styles.textSmall}>
            {part}
          </span>
        );
      }
      if (idx < parts.length - 1) {
        nodes.push(<br key={`${keyPrefix}br-${idx}`} />);
      }
    });
    return nodes;
  };

  // 해시태그 및 멘션 매칭 (Flutter caption.dart의 _tokenRegex와 동일)
  const tokenRegex = /(#[^\s#]+|@((?:[a-z0-9]+[._])*[a-z0-9]+))/gu;

  // 토큰 수집
  const tokens: { token: string; index: number; mentionName?: string }[] = [];
  let match: RegExpExecArray | null;
  while ((match = tokenRegex.exec(text)) !== null) {
    tokens.push({
      token: match[0],
      index: match.index,
      mentionName: match[2] || undefined,
    });
  }

  // 토큰이 없으면 일반 텍스트 반환 (개행 처리 포함)
  if (tokens.length === 0) {
    return <>{renderTextWithLineBreaks(text, 'plain-')}</>;
  }

  // 텍스트/토큰 요소 배열 구성
  const elements: React.ReactNode[] = [];
  let lastIndex = 0;

  tokens.forEach((t, i) => {
    // 토큰 이전 텍스트 추가 (개행 처리)
    if (t.index > lastIndex) {
      const beforeText = text.substring(lastIndex, t.index);
      elements.push(...renderTextWithLineBreaks(beforeText, `text-${i}-`));
    }

    if (t.mentionName) {
      // 멘션 링크 추가
      elements.push(
        <Link className={styles.linkInline} key={`mention-${i}`} href={`/accounts/${encodeURIComponent(t.mentionName)}`}>
          <span className={styles.tokenLink}>{t.token}</span>
        </Link>
      );
    } else {
      // 해시태그 링크 추가
      const tagName = t.token.substring(1);
      elements.push(
        <Link className={styles.linkInline} key={`hashtag-${i}`} href={`/hashtags/${encodeURIComponent(tagName)}`}>
          <span className={styles.tokenLink}>#{tagName}</span>
        </Link>
      );
    }

    lastIndex = t.index + t.token.length;
  });

  // 마지막 토큰 이후 텍스트 추가 (개행 처리)
  if (lastIndex < text.length) {
    const tailText = text.substring(lastIndex);
    elements.push(...renderTextWithLineBreaks(tailText, 'text-end-'));
  }

  return <>{elements}</>;
};

export default Caption;
